import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	useNavigate,
	useParams,
	useSearch,
} from "@tanstack/react-router";
import { useContext } from "react";
import z from "zod";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { WorkflowRuleFullPage } from "@/components/Organisms/WorkflowRuleFullPage/WorkflowRuleFullPage";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { authQueryOptions } from "@/services/auth/auth.options";
import { TicketTypeDrawerContext } from "@/utils/ticket-type-detail/ticket-type-drawer-context";

const SearchSchema = z.object({
	ruleTab: z.enum(["details", "activity"]).optional().default("details"),
});

const ROUTE_ID =
	"/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/configuration/ticketTypes/$ticketTypeId/full/rules/$ruleId" as const;

export const Route = createFileRoute(ROUTE_ID)({
	component: OrgTicketTypeRuleDetailPage,
	validateSearch: (search) => SearchSchema.parse(search),
	params: {
		parse: (raw) => {
			const p = raw as {
				orgId: string;
				ticketTypeId: string;
				ruleId: string;
			};
			return {
				orgId: z.string().parse(p.orgId),
				ticketTypeId: z.string().parse(p.ticketTypeId),
				ruleId: z.string().parse(p.ruleId),
			};
		},
	},
});

function OrgTicketTypeRuleDetailPage() {
	const ctx = useContext(TicketTypeDrawerContext);
	const { t } = useAppTranslation();
	const navigate = useNavigate();
	const params = useParams({ from: ROUTE_ID });
	const search = useSearch({ from: ROUTE_ID });

	const canEditRuleQuery = useQuery(
		authQueryOptions.checkAction({
			name: "EditWorkflowRule",
			entityType: "WorkflowRule",
			entityId: params.ruleId,
		}),
	);
	const canEditRule = Boolean(canEditRuleQuery.data);

	const canDeleteRuleQuery = useQuery(
		authQueryOptions.checkAction({
			name: "DeleteWorkflowRule",
			entityType: "WorkflowRule",
			entityId: params.ruleId,
		}),
	);
	const canDeleteRule = Boolean(canDeleteRuleQuery.data);

	const canArchiveRuleQuery = useQuery(
		authQueryOptions.checkAction({
			name: "ArchiveWorkflowRule",
			entityType: "WorkflowRule",
			entityId: params.ruleId,
		}),
	);
	const canArchiveRule = Boolean(canArchiveRuleQuery.data);

	if (!ctx) {
		return (
			<div className="flex min-h-screen items-center justify-center">
				<LoadingIndicator type="line-simple" size="xl" supportingText />
			</div>
		);
	}

	const { ticketType } = ctx;

	return (
		<WorkflowRuleFullPage
			ruleId={params.ruleId}
			mode="organization"
			organizationId={params.orgId}
			lifecycleId={ticketType.lifecycleId}
			entityTypeId={params.ticketTypeId}
			canEdit={canEditRule}
			canDelete={canDeleteRule}
			canArchive={canArchiveRule}
			entityTypeLabel={t("admin.ticketTypes.table.title")}
			entityTypeName={ticketType.name ?? ""}
			activeTab={search.ruleTab}
			onTabChange={(ruleTab) => {
				void navigate({
					to: "/org-admin/$orgId/configuration/ticketTypes/$ticketTypeId/full/rules/$ruleId",
					params: {
						orgId: params.orgId,
						ticketTypeId: params.ticketTypeId,
						ruleId: params.ruleId,
					},
					search: { tab: "rules", mode: "view", presentation: "full", ruleTab },
				});
			}}
			onBackToRules={() => {
				void navigate({
					to: "/org-admin/$orgId/configuration/ticketTypes/$ticketTypeId/full",
					params: { orgId: params.orgId, ticketTypeId: params.ticketTypeId },
					search: { tab: "rules", mode: "view", presentation: "full" },
				});
			}}
			onNavigateToEntityTypeList={() => {
				void navigate({
					to: "/org-admin/$orgId/configuration/ticketTypes",
					params: { orgId: params.orgId },
				});
			}}
			onNavigateToEntityTypeRules={() => {
				void navigate({
					to: "/org-admin/$orgId/configuration/ticketTypes/$ticketTypeId/full",
					params: { orgId: params.orgId, ticketTypeId: params.ticketTypeId },
					search: { tab: "rules", mode: "view", presentation: "full" },
				});
			}}
			onAfterAction={() => {
				void navigate({
					to: "/org-admin/$orgId/configuration/ticketTypes/$ticketTypeId/full",
					params: { orgId: params.orgId, ticketTypeId: params.ticketTypeId },
					search: { tab: "rules", mode: "view", presentation: "full" },
				});
			}}
		/>
	);
}
